import { useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import type { TopicInfo } from '../../types';
import { markdownToHtml } from '../../utils/markdown';
import styles from './InterpretationView.module.css';

interface InterpretationViewProps {
  chartId: string;
  topic: TopicInfo;
  onBack: () => void;
}

export function InterpretationView({ chartId, topic, onBack }: InterpretationViewProps) {
  const [text, setText] = useState('');
  const [streaming, setStreaming] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef(false);

  useEffect(() => {
    abortRef.current = false;
    let fullText = '';

    (async () => {
      try {
        for await (const event of api.streamInterpretation(chartId, topic.key)) {
          if (abortRef.current) break;

          if (event.error) {
            setError(event.error);
            setStreaming(false);
            return;
          }

          // The server retried after a failed validation: what we drew so far is the
          // degraded generation, so throw it away and render the new one from scratch.
          if (event.reset) {
            fullText = '';
            setText('');
            continue;
          }

          if (event.text) {
            fullText += event.text;
            setText(fullText);
          }

          if (event.done) {
            setStreaming(false);
            return;
          }
        }
        // Stream ended without explicit done
        setStreaming(false);
      } catch (err: unknown) {
        if (abortRef.current) return;
        const status = (err as { status?: number }).status;
        if (status === 402) {
          setError('Эта тема доступна по подписке');
        } else if (status === 429) {
          setError('Достигнут лимит запросов. Попробуйте позже');
        } else {
          setError('Ошибка соединения');
        }
        setStreaming(false);
      }
    })();

    return () => {
      abortRef.current = true;
    };
  }, [chartId, topic.key]);

  if (error && !text) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.headerIcon}>{topic.icon}</span>
          <span className={styles.headerTitle}>{topic.name}</span>
        </div>
        <div className={styles.errorBox}>
          <span className={styles.errorText}>{error}</span>
          <button className={styles.backButton} onClick={onBack}>
            Назад к карте
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.headerIcon}>{topic.icon}</span>
        <span className={styles.headerTitle}>{topic.name}</span>
      </div>

      <div className={styles.content}>
        <div className={styles.text} dangerouslySetInnerHTML={{ __html: markdownToHtml(text) }} />
        {streaming && (
          <div className={styles.generating}>
            <span>Генерирую</span>
            <span className={styles.dots}>
              <span />
              <span />
              <span />
            </span>
          </div>
        )}
      </div>

      {!streaming && (
        <button className={styles.backButton} onClick={onBack}>
          Назад к карте
        </button>
      )}
    </div>
  );
}
